import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchAllSymbols } from '../data/binance';
import { DEMO } from '../data/mode';
import { DEMO_SYMBOLS } from '../data/demo';
import { store, type SymbolState } from '../data/store';
import { C } from '../styles/palette';
import type { CanvasSurface } from '../lib/frame';
import { compact, pct, price as fmtPrice } from '../lib/num';
import { useCanvasPanel, useDismiss, usePersisted, useStoreTick, useStructure } from './hooks';

type SortKey = 'manual' | 'change' | 'volume' | 'symbol';

/**
 * Multi-symbol watchlist.
 *
 * Text rows are DOM (they need hover, drag-reorder and click targets) but the
 * sparklines are canvas, one per row, each with its own dirty check against
 * that symbol's 1m kline sequence. A row whose symbol has not printed since
 * the last frame costs a single integer comparison.
 */
export function Watchlist() {
  useStructure();
  useStoreTick();

  const [sort, setSort] = usePersisted<SortKey>('terminal.watchlist.sort', 'manual');
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [universe, setUniverse] = useState<string[]>(DEMO ? DEMO_SYMBOLS : []);
  const dragFrom = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const addRef = useDismiss<HTMLDivElement>(adding, () => {
    setAdding(false);
    setQuery('');
  });

  // The full symbol list is ~2,800 entries and only needed once the user opens
  // the search, so it is fetched lazily rather than at boot.
  useEffect(() => {
    if (!adding || universe.length > 0) return;
    let alive = true;
    void fetchAllSymbols()
      .then((s) => alive && setUniverse(s))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [adding, universe.length]);

  const symbols = store.symbols;

  const ordered = useMemo(() => {
    const list = symbols.map((s) => ({ symbol: s, st: store.get(s) }));
    switch (sort) {
      case 'change':
        return list.sort((a, b) => (Number.isFinite(b.st.ticker.changePct) ? b.st.ticker.changePct : -Infinity) - (Number.isFinite(a.st.ticker.changePct) ? a.st.ticker.changePct : -Infinity));
      case 'volume':
        return list.sort((a, b) => (b.st.ticker.quoteVolume || 0) - (a.st.ticker.quoteVolume || 0));
      case 'symbol':
        return list.sort((a, b) => a.symbol.localeCompare(b.symbol));
      default:
        return list;
    }
  }, [symbols, sort, store.version]);

  const matches = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return [];
    const pool = universe.length ? universe : [];
    const starts: string[] = [];
    const contains: string[] = [];
    for (const s of pool) {
      if (s.startsWith(q)) starts.push(s);
      else if (s.includes(q)) contains.push(s);
      if (starts.length >= 20) break;
    }
    return [...starts, ...contains].slice(0, 20);
  }, [query, universe]);

  const commitAdd = useCallback(
    (sym: string) => {
      const s = sym.trim().toUpperCase();
      if (!s) return;
      store.addSymbol(s);
      store.setActive(s);
      setQuery('');
      setAdding(false);
    },
    [],
  );

  const onDrop = useCallback(
    (to: number) => {
      const from = dragFrom.current;
      dragFrom.current = null;
      setDragOver(null);
      if (from === null || from === to) return;
      if (sort !== 'manual') return;
      store.reorderSymbol(from, to);
    },
    [sort],
  );

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">Watchlist</span>
        <span className="sub">{symbols.length}</span>
        <span className="spacer" />
        <div className="seg">
          {(['manual', 'change', 'volume', 'symbol'] as SortKey[]).map((k) => (
            <button
              key={k}
              className={sort === k ? 'on' : ''}
              onClick={() => setSort(k)}
              title={`Sort by ${k}`}
            >
              {k === 'manual' ? '≡' : k === 'change' ? '%' : k === 'volume' ? 'V' : 'A'}
            </button>
          ))}
        </div>
        <button
          className="btn sm ghost"
          onClick={() => setAdding((a) => !a)}
          title="Add a symbol"
        >
          +
        </button>
      </div>

      {adding && (
        <div className="wl-add" ref={addRef}>
          <input
            className="input"
            autoFocus
            placeholder="Search symbols — e.g. AVAX"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitAdd(matches[0] ?? query);
              if (e.key === 'Escape') {
                setAdding(false);
                setQuery('');
              }
            }}
          />
          {query && (
            <div className="wl-results">
              {matches.length === 0 && (
                <div className="pop-item muted">
                  {universe.length === 0 ? 'loading symbol list…' : 'no match'}
                </div>
              )}
              {matches.map((m) => (
                <div
                  key={m}
                  className={`pop-item ${symbols.includes(m) ? 'on' : ''}`}
                  onClick={() => commitAdd(m)}
                >
                  {m}
                  {symbols.includes(m) && <span className="dim">· on list</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="panel-body wl-scroll">
        <div className="wl-head">
          <span>Symbol</span>
          <span className="r">Last</span>
          <span className="r">24h</span>
          <span className="c">1h</span>
          <span className="r">Vol</span>
        </div>
        {ordered.map((row, i) => (
          <WatchRow
            key={row.symbol}
            symbol={row.symbol}
            st={row.st}
            active={row.symbol === store.active}
            index={i}
            draggable={sort === 'manual'}
            dragOver={dragOver === i}
            onDragStart={() => (dragFrom.current = i)}
            onDragOver={() => setDragOver(i)}
            onDrop={() => onDrop(i)}
          />
        ))}
      </div>

      <Breadth rows={ordered} />
    </div>
  );
}

/**
 * Advance/decline summary. The watchlist is usually shorter than its panel,
 * and an empty half-panel reads as something failing to load — this puts the
 * one number a trader actually wants from a list of symbols where that space
 * was going to waste.
 */
function Breadth({ rows }: { rows: { symbol: string; st: SymbolState }[] }) {
  let up = 0;
  let down = 0;
  let flat = 0;
  let quoteVol = 0;
  for (const { st } of rows) {
    const c = st.ticker.changePct;
    if (!Number.isFinite(c)) flat++;
    else if (c > 0) up++;
    else if (c < 0) down++;
    else flat++;
    if (Number.isFinite(st.ticker.quoteVolume)) quoteVol += st.ticker.quoteVolume;
  }
  const total = Math.max(1, up + down + flat);

  return (
    <div className="wl-breadth" title="Advancing vs declining on this list, and combined 24h quote volume">
      <div className="wl-breadth-bar">
        <div className="up-seg" style={{ width: `${((up / total) * 100).toFixed(1)}%` }} />
        <div className="flat-seg" style={{ width: `${((flat / total) * 100).toFixed(1)}%` }} />
      </div>
      <span className="up num">{up}</span>
      <span className="dim">/</span>
      <span className="down num">{down}</span>
      <span className="spacer" />
      <span className="mono-label">24h vol</span>
      <span className="num muted">{compact(quoteVol)}</span>
    </div>
  );
}

interface RowProps {
  symbol: string;
  st: SymbolState;
  active: boolean;
  index: number;
  draggable: boolean;
  dragOver: boolean;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
}

function WatchRow({
  symbol,
  st,
  active,
  draggable,
  dragOver,
  onDragStart,
  onDragOver,
  onDrop,
}: RowProps) {
  const t = st.ticker;
  const up = t.changePct >= 0;

  // Flash the row background on each tick change. Keyed on the tick counter so
  // React restarts the CSS animation rather than leaving it mid-flight.
  const prev = useRef(t.last);
  const dir = t.last > prev.current ? 1 : t.last < prev.current ? -1 : 0;
  const flashKey = useRef(0);
  if (dir !== 0) {
    prev.current = t.last;
    flashKey.current++;
  }

  return (
    <div
      className={`wl-row ${active ? 'active' : ''} ${dragOver ? 'over' : ''}`}
      onClick={() => store.setActive(symbol)}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      title={`${symbol} — click to focus`}
    >
      <span className="wl-sym truncate">
        {draggable && <span className="wl-grip">⠿</span>}
        {symbol.replace(/USDT$/, '')}
        <span className="dim wl-quote">{symbol.endsWith('USDT') ? 'USDT' : ''}</span>
      </span>

      <span
        key={flashKey.current}
        className={`wl-last num ${dir > 0 ? 'flash-up up' : dir < 0 ? 'flash-down down' : ''}`}
      >
        {Number.isFinite(t.last) ? fmtPrice(t.last, st.pricePrecision) : '—'}
      </span>

      <span className={`wl-chg num ${up ? 'up' : 'down'}`}>
        {Number.isFinite(t.changePct) ? pct(t.changePct) : '—'}
      </span>

      <span className="wl-spark">
        <Sparkline st={st} up={up} />
      </span>

      <span className="wl-vol num muted" title="24h quote volume">
        {Number.isFinite(t.quoteVolume) ? compact(t.quoteVolume) : '—'}
      </span>

      <button
        className="wl-remove"
        title={`Remove ${symbol}`}
        onClick={(e) => {
          e.stopPropagation();
          store.removeSymbol(symbol);
        }}
      >
        ×
      </button>
    </div>
  );
}

/** Last ~60 one-minute closes, drawn as a filled sparkline. */
function Sparkline({ st, up }: { st: SymbolState; up: boolean }) {
  const lastSeq = useRef(-1);
  const series = st.series('1m');

  const draw = useCallback(
    (surface: CanvasSurface) => {
      const { ctx, width, height } = surface;
      ctx.clearRect(0, 0, width, height);
      lastSeq.current = series.seq;

      const n = series.length;
      if (n < 2 || width < 4 || height < 4) return;

      const count = Math.min(n, 60);
      const start = n - count;
      const close = series.close.data;

      let min = Infinity;
      let max = -Infinity;
      for (let i = start; i < n; i++) {
        const v = close[i];
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (!Number.isFinite(min) || max === min) {
        // A perfectly flat window still deserves a line, drawn mid-height.
        ctx.strokeStyle = C.text3;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        ctx.lineTo(width, height / 2);
        ctx.stroke();
        return;
      }

      const pad = 2;
      const h = height - pad * 2;
      const xOf = (i: number) => ((i - start) / (count - 1)) * (width - 2) + 1;
      const yOf = (v: number) => pad + h - ((v - min) / (max - min)) * h;

      const color = up ? C.up : C.down;

      ctx.beginPath();
      ctx.moveTo(xOf(start), yOf(close[start]));
      for (let i = start + 1; i < n; i++) ctx.lineTo(xOf(i), yOf(close[i]));

      // Fill under the line before stroking it.
      ctx.save();
      ctx.lineTo(xOf(n - 1), height);
      ctx.lineTo(xOf(start), height);
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, up ? 'rgba(46,189,133,0.30)' : 'rgba(246,70,93,0.30)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.restore();

      ctx.beginPath();
      ctx.moveTo(xOf(start), yOf(close[start]));
      for (let i = start + 1; i < n; i++) ctx.lineTo(xOf(i), yOf(close[i]));
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.lineJoin = 'round';
      ctx.stroke();

      // Endpoint marker so the current level is unambiguous.
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(xOf(n - 1), yOf(close[n - 1]), 1.6, 0, Math.PI * 2);
      ctx.fill();
    },
    [series, up],
  );

  const ref = useCanvasPanel(draw, {
    priority: 15,
    dirty: () => series.seq !== lastSeq.current,
  });

  return <canvas ref={ref} className="spark-canvas" />;
}
